import {
  CurrencyCode,
  DeductionApplicationType,
  enumSalesItemLineType,
  enumUnit,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { atom, getDefaultStore, Getter, Setter } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import {
  $itemsInterfaceEntries,
  $recalculateDeductionsTotal,
  DeductionChangeAction,
  DeductionChangeCallbackFn,
} from '../_components/_shared/ItemsInterface/_state';
import {
  enumLineItemKind,
  ItemsListEntry,
  NormalizedDeduction,
  NormalizedFreightCharge,
  NormalizedLineItem,
} from '../_components/_shared/ItemsInterface/_types';
import { parseSalesLoadLineItem } from '../_components/_shared/ItemsInterface/utils/parseSalesLoadLineItem';
import { loadDeductionCreateAtom } from '../_graphql/mutationLoadDeductionCreate';
import { loadDeductionDeleteAtom } from '../_graphql/mutationLoadDeductionDelete';
import { loadDeductionUpdateAtom } from '../_graphql/mutationLoadDeductionUpdate';
import { loadUpdateAtom } from '../_graphql/mutationLoadUpdate';
import {
  enumLoadWeightType,
  LoadDeductionRecord,
  LoadFormLineItem,
  LoadLineItemRecord,
} from '../types/load';
import { WeightUnitMode } from '../types/scaleTickets';
import { toMoney, toMoneyInput } from '../utils/money';
import { toWeightInput } from '../utils/weight';

import {
  $materialsTempToRealIdMapById,
  createMaterialsActions,
  FreightData,
  MutationLifecycle,
  resolveId,
} from './materialsForm.state';
import { mapWith } from './utils/mapAtomHelpers';

// ──────────────────────────────────────────────────────────────────────────────
// Outbound-specific: weight unit mode (separate localStorage key)
// ──────────────────────────────────────────────────────────────────────────────

export const $outboundWeightUnitMode = atomWithStorage<WeightUnitMode>(
  'scaleTicket.outbound.weightUnitMode',
  'scale',
);

// ──────────────────────────────────────────────────────────────────────────────
// Outbound-specific: normalization & payload building
// ──────────────────────────────────────────────────────────────────────────────

export const normalizeOutboundEntries = (
  lineItems: LoadLineItemRecord[],
  deductions: LoadDeductionRecord[],
  freight?: FreightData | null,
): ItemsListEntry[] => {
  const normalizedLineItems: ItemsListEntry[] = lineItems.map((item) =>
    parseSalesLoadLineItem.toNormalized(item as unknown as LoadFormLineItem),
  );

  const normalizedDeductions: NormalizedDeduction[] = deductions.map((d) => ({
    kind: 'Deduction' as const,
    id: d.id,
    deductionTypeId: d.deductionTypeId,
    deductionTypeName: d.deductionType?.name ?? null,
    applicationType: d.applicationType as 'Weight' | 'Price',
    weightValue: d.weightValue ?? null,
    priceValue: d.priceValue ?? null,
    calculatedAmount: d.calculatedAmount ?? null,
    pricingUnit: d.weightValue?.unit ?? enumUnit.Pounds,
    percentage: d.percentage ?? null,
    loadLineItemId: d.loadLineItemId ?? null,
    notes: d.notes ?? null,
  }));

  const hasFreight = freight?.freightCost || freight?.freightTerms;
  const normalizedFreight: NormalizedFreightCharge[] = hasFreight
    ? [
        {
          kind: 'FreightCharge',
          id: 'server-freight',
          freightCost: freight.freightCost ?? null,
          notes: freight.freightTerms ?? null,
        },
      ]
    : [];

  return [
    ...normalizedLineItems,
    ...normalizedDeductions,
    ...normalizedFreight,
  ];
};

const toLineItemPayload = (
  entry: NormalizedLineItem,
  tempToRealMap: Map<string, string>,
) => {
  const record = parseSalesLoadLineItem.fromNormalized(entry);
  const resolvedItemId = resolveId(entry.id, tempToRealMap);
  return {
    ...(resolvedItemId ? { id: resolvedItemId } : {}),
    type: record.type ?? enumSalesItemLineType.Raw,
    itemId: record.itemId ?? null,
    tagId: record.tagId ?? null,
    weights: record.weights
      ? {
          [enumLoadWeightType.Gross]: toWeightInput(
            record.weights[enumLoadWeightType.Gross],
          ),
          [enumLoadWeightType.Tare]: toWeightInput(
            record.weights[enumLoadWeightType.Tare],
          ),
          [enumLoadWeightType.Net]: toWeightInput(
            record.weights[enumLoadWeightType.Net],
          ),
        }
      : null,
    unitPrice: record.unitPrice ? toMoneyInput(record.unitPrice) : null,
    pricingUnit: record.pricingUnit ?? enumUnit.Pounds,
  };
};

const buildOutboundMutationPayload = (
  entries: ItemsListEntry[],
  tempToRealMap: Map<string, string>,
  weights?: { heavyWeight: Weight | null; lightWeight: Weight | null },
) => {
  const lineItems = entries.filter(
    (e): e is NormalizedLineItem => e.kind === enumLineItemKind.LineItem,
  );
  const freightEntry = entries.find(
    (e): e is NormalizedFreightCharge =>
      e.kind === enumLineItemKind.FreightCharge,
  );

  return {
    freightCost: freightEntry?.freightCost
      ? toMoneyInput(freightEntry.freightCost)
      : null,
    freightTerms: freightEntry?.notes ?? null,
    ...(lineItems.length > 0
      ? {
          lineItems: lineItems.map((entry) =>
            toLineItemPayload(entry, tempToRealMap),
          ),
        }
      : {}),
    ...(weights?.heavyWeight ? { heavyWeight: weights.heavyWeight } : {}),
    ...(weights?.lightWeight ? { lightWeight: weights.lightWeight } : {}),
  };
};

const setupOutboundMutationAtom = (lifecycle: MutationLifecycle) => {
  const $mutation = loadUpdateAtom({
    before: (get, set, { params }) => {
      lifecycle.onBefore(get, set, params.id);
    },
    onSuccess: (get, set, { params, result }) => {
      lifecycle.onSuccess(
        get,
        set,
        params.id,
        params.lineItems,
        undefined,
        {
          lineItems: result?.tradeLoadUpdate?.load?.lineItems ?? [],
          deductions: [],
        },
        params.failedToSaveMessage,
      );
    },
    onError: (get, set, { params }) => {
      lifecycle.onError(get, set, params.id, params.failedToSaveMessage);
    },
  });

  return atom(
    null,
    (
      _get: Getter,
      set: Setter,
      ctx: {
        ticketId: string;
        failedToSaveMessage: ((label: string) => string) | undefined;
        entries: ItemsListEntry[];
        tempToRealMap: Map<string, string>;
        weights:
          | { heavyWeight: Weight | null; lightWeight: Weight | null }
          | undefined;
      },
    ) => {
      set($mutation, {
        id: ctx.ticketId,
        failedToSaveMessage: ctx.failedToSaveMessage,
        ...buildOutboundMutationPayload(
          ctx.entries,
          ctx.tempToRealMap,
          ctx.weights,
        ),
      });
    },
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Standalone deduction mutation atoms
// ──────────────────────────────────────────────────────────────────────────────

const reconcileTempId = (
  get: Getter,
  set: Setter,
  loadId: string,
  tempDeductionTypeId: string | null,
  tempApplicationType: string,
  createdId: string,
  calculatedAmount: { amount: number; currencyCode: string } | null,
) => {
  const state = get($itemsInterfaceEntries);
  const tempEntry = state.items.find(
    (e) =>
      e.kind === enumLineItemKind.Deduction &&
      e.id.startsWith('temp-') &&
      (e as NormalizedDeduction).deductionTypeId === tempDeductionTypeId &&
      (e as NormalizedDeduction).applicationType === tempApplicationType,
  ) as NormalizedDeduction | undefined;

  if (tempEntry) {
    const money = calculatedAmount
      ? toMoney({
          amount: calculatedAmount.amount,
          currencyCode: calculatedAmount.currencyCode as CurrencyCode,
        })
      : null;
    const updatedItems = state.items.map((e) =>
      e.id === tempEntry.id
        ? { ...e, id: createdId, calculatedAmount: money }
        : e,
    );
    set($itemsInterfaceEntries, { ...state, items: updatedItems });

    const tempToRealMaps = get($materialsTempToRealIdMapById);
    const ticketMap = tempToRealMaps.get(loadId) ?? new Map<string, string>();
    const newMap = new Map(ticketMap);
    newMap.set(tempEntry.id, createdId);
    set($materialsTempToRealIdMapById, mapWith(tempToRealMaps, loadId, newMap));
  }

  set($recalculateDeductionsTotal);
};

const $deductionCreate = loadDeductionCreateAtom({
  onSuccess: (get, set, { params, result }) => {
    const created = result?.tradeLoadDeductionCreate;
    if (!created) return;

    reconcileTempId(
      get,
      set,
      params.loadId,
      created.deductionTypeId,
      created.applicationType,
      created.id,
      created.calculatedAmount ?? null,
    );
  },
});

const $deductionUpdate = loadDeductionUpdateAtom({
  onSuccess: (get, set, { result }) => {
    const updated = result?.tradeLoadDeductionUpdate;
    if (!updated) return;

    const money = updated.calculatedAmount
      ? toMoney({
          amount: updated.calculatedAmount.amount,
          currencyCode: updated.calculatedAmount.currencyCode as CurrencyCode,
        })
      : null;
    const state = get($itemsInterfaceEntries);
    const updatedItems = state.items.map((e) =>
      e.id === updated.id && e.kind === enumLineItemKind.Deduction
        ? { ...e, calculatedAmount: money }
        : e,
    );
    set($itemsInterfaceEntries, { ...state, items: updatedItems });
    set($recalculateDeductionsTotal);
  },
});

const $deductionDelete = loadDeductionDeleteAtom({
  onSuccess: (_get, set) => {
    set($recalculateDeductionsTotal);
  },
});

// ──────────────────────────────────────────────────────────────────────────────
// Deduction change callback factory
// ──────────────────────────────────────────────────────────────────────────────

export const createOutboundDeductionChangeHandler = (
  loadId: string,
  facilityId: string,
): DeductionChangeCallbackFn => {
  const store = getDefaultStore();

  return (action: DeductionChangeAction, deduction: NormalizedDeduction) => {
    switch (action) {
      case 'create': {
        store.set($deductionCreate, {
          loadId,
          facilityId,
          deductionTypeId: deduction.deductionTypeId ?? '',
          applicationType:
            deduction.applicationType as DeductionApplicationType,
          weightValue: toWeightInput(deduction.weightValue),
          priceValue: toMoneyInput(deduction.priceValue),
          percentage: deduction.percentage ?? null,
          notes: deduction.notes ?? null,
        });
        break;
      }
      case 'update': {
        const resolvedId = deduction.id.startsWith('temp-')
          ? null
          : deduction.id;
        if (!resolvedId) return;

        store.set($deductionUpdate, {
          id: resolvedId,
          deductionTypeId: deduction.deductionTypeId,
          applicationType:
            deduction.applicationType as DeductionApplicationType,
          weightValue: toWeightInput(deduction.weightValue),
          priceValue: toMoneyInput(deduction.priceValue),
          percentage: deduction.percentage ?? null,
          notes: deduction.notes ?? null,
        });
        break;
      }
      case 'delete': {
        const resolvedId = deduction.id.startsWith('temp-')
          ? null
          : deduction.id;
        if (!resolvedId) return;

        store.set($deductionDelete, { id: resolvedId });
        break;
      }
    }
  };
};

// ──────────────────────────────────────────────────────────────────────────────
// Factory call
// ──────────────────────────────────────────────────────────────────────────────

const outboundActions = createMaterialsActions({
  debugPrefix: 'outbound',
  normalizeEntries: normalizeOutboundEntries,
  setupMutationAtom: setupOutboundMutationAtom,
});

// ──────────────────────────────────────────────────────────────────────────────
// Exports
// ──────────────────────────────────────────────────────────────────────────────

export const $saveOutboundMaterials = outboundActions.$save;
export const $syncOutboundMaterialsForm = outboundActions.$sync;
